import { useState, forwardRef, useEffect } from 'react';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { getBlockLots } from 'src/pages/Permits/common/getBlockLots';
import { useDispatch } from 'react-redux';
import { updateFormData } from 'src/redux-store/slices/constructionFormSlice';

interface BlockLotOption {
  PropAddress: string;
  BlockLot: string;
  OwnerName?: string;
}

interface EstablishmentInfoProps {
  formData?: {
    establishmentName: string;
    establishmentContactName: string;
    blockLot?: string;
    establishmentAddress?: string;
    establishmentTown: string;
    establishmentEmail: string;
    establishmentPhone: string;
  };
  handleSubmit: (val: any) => void;
  setFieldValue: (field: string, value: any) => void;
  isButtonHide?: boolean;
  setIsButtonHide?: (flag: boolean) => void;
  isEdit?: boolean;
}

const EstablishmentInfo = forwardRef<any, EstablishmentInfoProps>(
  ({ 
    formData,
    setFieldValue,
    isButtonHide,
    handleSubmit
  }, ref) => {
    const dispatch = useDispatch();
    const [blockLotsOptions, setBlockLotsOptions] = useState([]);
    const [formValues, setFormValues] = useState({
      blockLot: formData?.blockLot || '',
      establishmentAddress: formData?.establishmentAddress || '',
      establishmentName: formData?.establishmentName || '',
      establishmentContactName: formData?.establishmentContactName || '',
      establishmentTown: formData?.establishmentTown || '',
      establishmentEmail: formData?.establishmentEmail || '',
      establishmentPhone: formData?.establishmentPhone || '',
    });

    // Load block lots on initial render if needed
    useEffect(() => {
      if (formData?.blockLot) {
        getBlockLots(formData.blockLot).then(data => {
          setBlockLotsOptions(data);
        });
      }
    }, [formData?.blockLot]);

    const handleFieldChange = async (val: string | BlockLotOption, e: any) => {
      const { name } = e;
      
      if (name === 'blockLot') {
        if (typeof val === 'object' && val !== null) {
          // User selected an option from the dropdown
          const blockLotString = val?.BlockLot || '';
          const streetAddressValue = val?.PropAddress || '';
          const ownerNameValue = val?.OwnerName || '';
          
          // Update the form fields directly
          setFormValues(prev => ({
            ...prev,
            blockLot: blockLotString,
            establishmentAddress: streetAddressValue
          }));
          
          // Update the formik values
          setFieldValue('blockLot', blockLotString);
          setFieldValue('establishmentAddress', streetAddressValue);
          
          // Update redux store
          dispatch(updateFormData({ blockLot: blockLotString }));
          dispatch(updateFormData({ establishmentAddress: streetAddressValue }));
          dispatch(updateFormData({ OwnersName: ownerNameValue }));
        } else if (typeof val === 'string') {
          // User is typing in the field
          if (val !== '') {
            const blockLotsData = await getBlockLots(val);
            setBlockLotsOptions(blockLotsData);
          }
          
          // Update local state and redux
          setFormValues(prev => ({
            ...prev,
            blockLot: val
          }));
          
          // Update Formik
          setFieldValue('blockLot', val);
          
          dispatch(updateFormData({ blockLot: val }));
        }
      } else {
        // Handle other field changes
        setFormValues(prev => ({
          ...prev,
          [name]: val
        }));
        
        // Update Formik value - THIS WAS MISSING
        setFieldValue(name, val);
        
        dispatch(updateFormData({ [name]: val }));
      }
    };
       
    const fields: Fields[] = [
      {
        fieldName: 'blockLot',
        initialValue: formValues.blockLot,
        fieldLabel: 'Block/Lot',
        fieldType: 'autocomplete',
        heading: 'Establishment Info',
        options: blockLotsOptions,
        required: true,
        dependency: handleFieldChange
      },
      {
        fieldName: 'streetAddress',
        initialValue: formValues.establishmentAddress,
        fieldLabel: 'Street Address',
        fieldType: 'text',
        required: true,
        dependency: handleFieldChange
      },
      {
        fieldName: 'establishmentName',
        initialValue: formValues.establishmentName,
        fieldLabel: 'Establishment Name',
        fieldType: 'text',
        required: true,
        dependency: handleFieldChange
      },
      {
        fieldName: 'establishmentContactName',
        initialValue: formValues.establishmentContactName,
        fieldLabel: 'Manager/Establishment Contact Name',
        fieldType: 'text',
        required: true,
        dependency: handleFieldChange
      },
      {
        fieldName: 'establishmentTown',
        initialValue: formValues.establishmentTown,
        fieldLabel: 'Town',
        fieldType: 'text',
        required: true,
        dependency: handleFieldChange
      },
      {
        fieldName: 'establishmentEmail',
        initialValue: formValues.establishmentEmail,
        fieldLabel: 'Email Address',
        fieldType: 'email',
        required: true,
        dependency: handleFieldChange
      },
      {
        fieldName: 'establishmentPhone',
        initialValue: formValues.establishmentPhone,
        fieldLabel: 'Phone Number',
        fieldType: 'mobile',
        required: true,
        dependency: handleFieldChange
      }
    ];

    return (
      <FormikForm
        innerRef={ref}
        allFields={fields}
        formMethod={handleSubmit}
        heading={'Establishment Information'}
        buttonTitle={['Cancel']}
        buttonPlacement={'right'}
        content={<></>}
        isButtonHide={isButtonHide}
      />
    );
  }
);

EstablishmentInfo.displayName = 'EstablishmentInfo';

export default EstablishmentInfo;